import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const MODELS = [
  { id: "claude-sonnet-4-6", name: "Claude Sonnet 4.6", badge: "Optimal for Tech Troubleshooting" },
  { id: "gpt-5.4", name: "GPT 5.4", badge: "Best for Quick Customer Service" },
  { id: "gemini-3.1-pro", name: "Gemini 3.1 Pro", badge: "Great for Analysis & Docs" },
];

export const VOICES = [
  { id: "alloy", name: "Alloy", badge: "Neutral & balanced" },
  { id: "onyx", name: "Onyx", badge: "Deep & authoritative" },
  { id: "nova", name: "Nova", badge: "Energetic & upbeat" },
  { id: "shimmer", name: "Shimmer", badge: "Bright & cheerful" },
  { id: "echo", name: "Echo", badge: "Smooth & calm" },
  { id: "fable", name: "Fable", badge: "Warm & expressive" },
];

// Per-device owner id — scopes a guest's conversations to their own device (privacy).
const OWNER_KEY = "apex_owner_id";
export const getOwnerId = () => {
  let o = localStorage.getItem(OWNER_KEY);
  if (!o) {
    o = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(OWNER_KEY, o);
  }
  return o;
};
const ownerCfg = () => ({ headers: { "X-Session-Owner": getOwnerId() } });

export const fetchSessions = () => axios.get(`${API}/sessions`, ownerCfg()).then((r) => r.data);
export const fetchMessages = (id) => axios.get(`${API}/sessions/${id}/messages`, ownerCfg()).then((r) => r.data);
export const fetchContext = (id) => axios.get(`${API}/sessions/${id}/context`, ownerCfg()).then((r) => r.data);
export const deleteSession = (id) => axios.delete(`${API}/sessions/${id}`, ownerCfg()).then((r) => r.data);

// ---- Staff auth (delegated to timekeeper) ----
const STAFF_TOKEN_KEY = "apex_staff_token";
export const getStaffToken = () => localStorage.getItem(STAFF_TOKEN_KEY) || "";
export const setStaffToken = (t) => (t ? localStorage.setItem(STAFF_TOKEN_KEY, t) : localStorage.removeItem(STAFF_TOKEN_KEY));
const authCfg = () => ({ headers: { Authorization: `Bearer ${getStaffToken()}` } });

export const staffLogin = (username, password) => axios.post(`${API}/staff/login`, { username, password }).then((r) => r.data);
export const staffMe = () => axios.get(`${API}/staff/me`, authCfg()).then((r) => r.data);
export const getKnowledge = () => axios.get(`${API}/tech/knowledge`, authCfg()).then((r) => r.data);
export const addKnowledge = (title, resolution) => axios.post(`${API}/tech/knowledge`, { title, resolution }, authCfg()).then((r) => r.data);
export const ingestKnowledge = (file) => {
  const fd = new FormData();
  fd.append("file", file, file.name);
  return axios.post(`${API}/tech/knowledge/ingest`, fd, authCfg()).then((r) => r.data);
};
export const getFollowups = () => axios.get(`${API}/tech/followups`, authCfg()).then((r) => r.data);
export const addFollowup = (body) => axios.post(`${API}/tech/followups`, body, authCfg()).then((r) => r.data);
export const toggleFollowup = (id) => axios.patch(`${API}/tech/followups/${id}`, {}, authCfg()).then((r) => r.data);
export const getBills = () => axios.get(`${API}/staff/bills`, authCfg()).then((r) => r.data);
export const getStaffOverview = () => axios.get(`${API}/staff/overview`, authCfg()).then((r) => r.data);

// ---- Customer self-help knowledge base ----
export const getCustomerKnowledge = () => axios.get(`${API}/customer/knowledge`).then((r) => r.data);
export const addCustomerKnowledge = (body) => axios.post(`${API}/tech/customer-knowledge`, body, authCfg()).then((r) => r.data);
export const deleteCustomerKnowledge = (id) => axios.delete(`${API}/tech/customer-knowledge/${id}`, authCfg()).then((r) => r.data);
export const ingestCustomerKnowledge = (file) => {
  const fd = new FormData();
  fd.append("file", file, file.name);
  return axios.post(`${API}/tech/customer-knowledge/ingest`, fd, authCfg()).then((r) => r.data);
};

export async function transcribeAudio(blob) {
  const fd = new FormData();
  fd.append("audio", blob, "recording.webm");
  const r = await fetch(`${API}/stt`, { method: "POST", body: fd });
  if (!r.ok) throw new Error(`STT ${r.status}`);
  return (await r.json()).text;
}

export async function synthesizeSpeech(text, voice) {
  const r = await fetch(`${API}/tts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, voice }),
  });
  if (!r.ok) throw new Error(`TTS ${r.status}`);
  return await r.blob();
}

// Streaming chat via fetch + ReadableStream (EventSource can't POST)
export async function streamChat({ session_id, message, mode, model, images }, { onDelta, onCard, onDone, onError }) {
  try {
    const headers = { "Content-Type": "application/json", "X-Session-Owner": getOwnerId() };
    const st = getStaffToken();
    if (st) headers.Authorization = `Bearer ${st}`;
    const res = await fetch(`${API}/chat/stream`, {
      method: "POST",
      headers,
      body: JSON.stringify({ session_id, message, mode, model, images: images || [] }),
    });
    if (!res.ok || !res.body) {
      onError && onError(`HTTP ${res.status}`);
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop();
      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith("data:")) continue;
        const payload = line.slice(5).trim();
        if (!payload) continue;
        let evt;
        try { evt = JSON.parse(payload); } catch { continue; }
        if (evt.type === "delta") onDelta && onDelta(evt.content);
        else if (evt.type === "card") onCard && onCard(evt.card);
        else if (evt.type === "done") onDone && onDone(evt.message_id);
        else if (evt.type === "error") onError && onError(evt.detail);
      }
    }
  } catch (e) {
    onError && onError(e.message);
  }
}
export const getJobs = (q = "", includeClosed = false) => axios.get(`${API}/staff/jobs`, { ...authCfg(), params: { q, include_closed: includeClosed } }).then((r) => r.data);
export const getTeam = () => axios.get(`${API}/staff/team`, authCfg()).then((r) => r.data);
export const assignJob = (id, userId) => axios.post(`${API}/staff/jobs/${id}/assign`, { user_id: userId }, authCfg()).then((r) => r.data);
